import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useStores } from '@/hooks';
import { AppTag } from '@pro-sto/ui';
import { formatPrice } from '@pro-sto/shared/utils';
import type { BookingStatus } from '@/stores/BookingsStore';
import './MobileViewBookingModal.css';

interface MobileViewBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingUuid: string;
  onCancel: () => void;
  onEdit?: () => void;
}

// Маппинг статусов на параметры тегов
const STATUS_CONFIG: Record<BookingStatus, { color: 'yellow' | 'blue' | 'green' | 'red'; label: string }> = {
  pending_confirmation: { color: 'yellow', label: 'Ожидает' },
  confirmed: { color: 'green', label: 'Подтверждена' },
  completed: { color: 'green', label: 'Завершено' },
  cancelled: { color: 'red', label: 'Отменён' },
};

export const MobileViewBookingModal = observer(({
  isOpen,
  onClose,
  bookingUuid,
  onCancel,
  onEdit,
}: MobileViewBookingModalProps) => {
  const { bookingsStore } = useStores();

  useEffect(() => {
    if (isOpen && bookingUuid) {
      bookingsStore.fetchBookingDetails(bookingUuid);
    }
  }, [isOpen, bookingUuid, bookingsStore]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const booking = bookingsStore.selectedBooking;

  if (bookingsStore.isLoadingDetails) {
    return (
      <div className="mobile-view-booking-modal">
        <div className="mobile-view-booking-modal__backdrop" onClick={onClose} />
        <div className="mobile-view-booking-modal__container">
          <div className="mobile-view-booking-modal__content">
            <div className="mobile-view-booking-modal__skeleton-header" />
            <div className="mobile-view-booking-modal__skeleton-body">
              <div className="mobile-view-booking-modal__skeleton-car" />
              <div className="mobile-view-booking-modal__skeleton-service" />
            </div>
            <div className="mobile-view-booking-modal__skeleton-footer">
              <div className="mobile-view-booking-modal__skeleton-button" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!booking) return null;

  // Форматирование даты и времени
  let formattedDate = '';
  let formattedTime = '';
  
  try {
    const startDate = parseISO(booking.start_time);
    const endDate = parseISO(booking.end_time);
    
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      formattedDate = 'Неверная дата';
      formattedTime = 'Неверное время';
    } else {
      formattedDate = format(startDate, 'd MMMM', { locale: ru });
      formattedTime = `${format(startDate, 'HH:mm')}-${format(endDate, 'HH:mm')}`;
    }
  } catch (error) {
    console.error('Error formatting dates:', error);
    formattedDate = 'Ошибка форматирования';
    formattedTime = 'Ошибка форматирования';
  }

  // Форматирование госномера. booking.car может отсутствовать (null), например если
  // клиент удалил машину после создания записи.
  const licensePlateData = booking.car?.license_plate;
  let licensePlateNumber = 'Не указан';
  let licensePlateRegion: string | null = null;

  if (typeof licensePlateData === 'string') {
    licensePlateNumber = licensePlateData || 'Не указан';
  } else if (licensePlateData && typeof licensePlateData === 'object') {
    const plate = licensePlateData as { number?: string; region?: string };
    licensePlateNumber = plate.number || 'Не указан';
    licensePlateRegion = plate.region || null;
  }

  // Форматирование длительности в минутах
  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} мин`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours} ч ${remainingMinutes} мин` : `${hours} ч`;
  };

  // Конфигурация статуса
  const statusConfig = STATUS_CONFIG[booking.status as BookingStatus] || STATUS_CONFIG.pending_confirmation;

  // Подсчет итоговой стоимости
  const totalPrice = booking.service.price + 
    (booking.additionalServices?.reduce((sum, service) => sum + service.price, 0) || 0);

  return (
    <div className="mobile-view-booking-modal" data-testid="mobile-view-booking-modal">
      <div className="mobile-view-booking-modal__backdrop" onClick={onClose} />
      
      <div className="mobile-view-booking-modal__container">
        <div className="mobile-view-booking-modal__content">
          {/* Header */}
          <div className="mobile-view-booking-modal__header">
            <button 
              className="mobile-view-booking-modal__back-button"
              onClick={onClose}
              aria-label="Назад"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <h2 className="mobile-view-booking-modal__title">Запись</h2>
            <div className="mobile-view-booking-modal__status-tag">
              <AppTag size="M" color={statusConfig.color}>
                {statusConfig.label}
              </AppTag>
            </div>
          </div>

          <div className="mobile-view-booking-modal__body">
            {/* Car Details */}
            <div className="mobile-view-booking-modal__car-section">
              <div className="mobile-view-booking-modal__car-info">
                <div className="mobile-view-booking-modal__car-name">
                  {booking.car ? `${booking.car.make} ${booking.car.model}` : 'Без автомобиля'}
                </div>
                <div className="mobile-view-booking-modal__car-plate-container">
                  <span className="mobile-view-booking-modal__car-plate-number">{licensePlateNumber}</span>
                  {licensePlateRegion && (
                    <>
                      <span className="mobile-view-booking-modal__car-plate-divider">|</span>
                      <span className="mobile-view-booking-modal__car-plate-region">{licensePlateRegion}</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Service Details */}
            <div className="mobile-view-booking-modal__service-section">
              <div className="mobile-view-booking-modal__datetime">
                {formattedDate}, {formattedTime}
              </div>

              <div className="mobile-view-booking-modal__divider" />

              <div className="mobile-view-booking-modal__service-list">
                {/* Main Service */}
                <div className="mobile-view-booking-modal__service-item">
                  <span className="mobile-view-booking-modal__service-name">{booking.service.name}</span>
                  <div className="mobile-view-booking-modal__service-info">
                    <span className="mobile-view-booking-modal__service-duration">
                      {formatDuration(booking.service.duration_minutes)}
                    </span>
                    <span className="mobile-view-booking-modal__service-price">{formatPrice(booking.service.price)}₽</span>
                  </div>
                </div>

                {/* Additional Services */}
                {booking.additionalServices && booking.additionalServices.length > 0 && (
                  <>
                    {booking.additionalServices.map((service) => (
                      <div key={service.uuid} className="mobile-view-booking-modal__service-item">
                        <span className="mobile-view-booking-modal__service-name">{service.name}</span>
                        <div className="mobile-view-booking-modal__service-info">
                          <span className="mobile-view-booking-modal__service-duration">
                            {formatDuration(service.duration_minutes)}
                          </span>
                          <span className="mobile-view-booking-modal__service-price">{formatPrice(service.price)}₽</span>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>

              <div className="mobile-view-booking-modal__divider" />

              {/* Total */}
              <div className="mobile-view-booking-modal__total">
                <span className="mobile-view-booking-modal__total-label">Итого</span>
                <span className="mobile-view-booking-modal__total-price">{formatPrice(totalPrice)}₽</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          {booking.status !== 'cancelled' && booking.status !== 'completed' && (
            <div className="mobile-view-booking-modal__footer">
              {onEdit && (
                <button 
                  className="mobile-view-booking-modal__edit-button"
                  onClick={onEdit}
                  data-testid="view-booking-edit-button"
                >
                  <span>Редактировать запись</span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.8333 3.16663L16.8333 5.16663M15.8333 2.16663L10.5 7.49996C10.1464 7.85358 9.96959 8.03039 9.8306 8.23283C9.70824 8.41183 9.61055 8.60737 9.5405 8.81407C9.46168 9.04706 9.42819 9.29584 9.36122 9.79341L9.00001 12.5L11.7066 12.1388C12.2042 12.0718 12.453 12.0383 12.6859 11.9595C12.8926 11.8895 13.0882 11.7918 13.2672 11.6694C13.4696 11.5304 13.6464 11.3536 14 11L19.3333 5.66663C19.6445 5.35541 19.8001 5.1998 19.9066 5.02143C20.1652 4.58429 20.1652 4.04896 19.9066 3.61182C19.8001 3.43346 19.6445 3.27784 19.3333 2.96663C19.0221 2.65541 18.8665 2.4998 18.6881 2.39328C18.251 2.13471 17.7156 2.13471 17.2785 2.39328C17.1001 2.4998 16.9445 2.65541 16.6333 2.96663L11.3 8.29996M4.66668 3.33329H3.50001C2.8537 3.33329 2.53054 3.33329 2.27248 3.39065C1.46738 3.57043 0.837098 4.20072 0.657319 5.00582C0.599976 5.26388 0.599976 5.58703 0.599976 6.23335V16.5C0.599976 17.1463 0.599976 17.4694 0.657319 17.7275C0.837098 18.5326 1.46738 19.1629 2.27248 19.3426C2.53054 19.4 2.8537 19.4 3.50001 19.4H13.7667C14.413 19.4 14.7361 19.4 14.9942 19.3426C15.7993 19.1629 16.4296 18.5326 16.6093 17.7275C16.6667 17.4694 16.6667 17.1463 16.6667 16.5V15.3333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
              <button 
                className="mobile-view-booking-modal__cancel-button"
                onClick={onCancel}
                data-testid="mobile-view-booking-modal-cancel"
              >
                <span>Отменить запись</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
